import React, { useState, useEffect } from 'react';
import { X, Zap, Loader2, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import ContactPicker from '../common/ContactPicker';
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Mid-pipeline entry for the consult-complete, ready-to-sign client in hand:
// one flow = create intake (staff_full door) -> conflict gate (mandatory, runs
// inline) -> hands off to the Quote modal at CONFLICT_CLEAR. The gate cannot
// be skipped — it compresses, it does not disappear.
export default function ReadyToSignModal({ isOpen, onClose, onConflictClear }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    adverse_party_name: '', practice_area: '', matter_description: '',
  });
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [linkedContact, setLinkedContact] = useState(null); // returning client
  const [phase, setPhase] = useState('form'); // form | creating | gating | review
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!isOpen) return;
    setPhase('form'); setError(null); setLinkedContact(null);
    axios.get(`${API_URL}/intake/practice-areas`, auth)
      .then((r) => setPracticeAreas(r.data || []))
      .catch(() => {});
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const go = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { setError('Name is required'); return; }
    if (!form.email.trim() && !form.phone.trim()) { setError('Email or phone is required'); return; }
    setError(null);
    try {
      setPhase('creating');
      const createRes = await axios.post(`${API_URL}/intake`, {
        contact_id: linkedContact?.contact_id || null,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        primary_phone: form.phone.trim() || null,
        adverse_party_name: form.adverse_party_name.trim() || null,
        practice_area: form.practice_area || null,
        matter_description: form.matter_description.trim() || null,
      }, auth);
      const intake = createRes.data;

      setPhase('gating');
      const gateRes = await axios.post(`${API_URL}/intake/${intake.id}/conflict-check`, {}, auth);
      const state = gateRes.data?.pipeline_state || gateRes.data?.state;

      if (state === 'conflict_clear') {
        onConflictClear({ ...intake, pipeline_state: 'conflict_clear' });
      } else {
        setPhase('review'); // conflict_review / conflicted — attorney review from the row
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create the intake');
      setPhase('form');
    }
  };

  const inputCls = "w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm focus:ring-2 focus:ring-cyan-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-virgo-navy-800 border border-virgo-teal-700/50 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-virgo-teal-700/40">
          <div className="text-white font-semibold flex items-center gap-2">
            <Zap size={18} className="text-virgo-gold-300" /> Ready to Sign — new intake at Quote
          </div>
          <button onClick={onClose} className="text-virgo-teal-400 hover:text-white"><X size={18} /></button>
        </div>

        {phase === 'review' ? (
          <div className="p-5 space-y-3">
            <p className="text-amber-300 text-sm flex items-start gap-2">
              <ShieldAlert size={18} className="shrink-0 mt-0.5" />
              The conflict gate flagged this intake for review. It was created and is in the list —
              resolve the conflict from the row (Review Conflicts), then quote from there.
            </p>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-virgo-teal-600 text-white text-sm">Got it</button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            {error && <p className="text-sm text-red-400">{error}</p>}
            {linkedContact ? (
              <div className="flex items-center justify-between rounded-lg border border-virgo-gold-500/40 bg-virgo-gold-500/10 px-3 py-2">
                <span className="text-sm text-virgo-gold-200">Returning client: <strong>{linkedContact.name}</strong></span>
                <button onClick={() => setLinkedContact(null)}
                  className="text-xs text-virgo-teal-400 hover:text-white underline">unlink</button>
              </div>
            ) : (
              <div>
                <p className="text-xs text-virgo-teal-400 mb-1">Returning client? Search existing contacts to prefill and link:</p>
                <ContactPicker
                  placeholder="Search contacts by name, email, or phone…"
                  onSelect={(c) => {
                    setLinkedContact(c);
                    setForm((f) => ({
                      ...f,
                      first_name: c.first_name || f.first_name,
                      last_name: c.last_name || f.last_name,
                      email: c.email || f.email,
                      phone: c.phone || f.phone,
                    }));
                  }}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <input value={form.first_name} onChange={set('first_name')} placeholder="First name *" className={inputCls} />
              <input value={form.last_name} onChange={set('last_name')} placeholder="Last name *" className={inputCls} />
              <input value={form.email} onChange={set('email')} placeholder="Email" type="email" className={inputCls} />
              <input value={form.phone} onChange={set('phone')} placeholder="Phone" type="tel" className={inputCls} />
            </div>
            <input value={form.adverse_party_name} onChange={set('adverse_party_name')}
              placeholder="Adverse party (for the conflict check)" className={inputCls} />
            <select value={form.practice_area} onChange={set('practice_area')} className={inputCls}>
              <option value="">Practice area…</option>
              {practiceAreas.map((p) => (
                <option key={p.value || p} value={p.value || p}>{p.label || p}</option>
              ))}
            </select>
            <textarea value={form.matter_description} onChange={set('matter_description')} rows={2}
              placeholder="What's the matter about (one line is fine)" className={inputCls} />
            <p className="text-xs text-virgo-teal-500">
              Creates the intake, runs the conflict gate, and drops you straight into the quote
              (pre-filled from the fee schedule, fully editable). Letter + sign&amp;pay are one
              click each after that.
            </p>
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={onClose} className="px-4 py-2 text-virgo-teal-300 hover:text-white text-sm">Cancel</button>
              <button onClick={go} disabled={phase !== 'form'}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium disabled:opacity-60 flex items-center gap-2">
                {phase === 'creating' ? (<><Loader2 className="animate-spin" size={14} /> Creating…</>)
                  : phase === 'gating' ? (<><Loader2 className="animate-spin" size={14} /> Conflict gate…</>)
                  : (<><Zap size={14} /> Create &amp; Quote</>)}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
